/***********************/
/*  Angular + external */
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { FlexLayoutModule } from "@angular/flex-layout";

import { AutoCompleteModule } from "@bfx/primeng/autocomplete";
import { ButtonModule } from "@bfx/primeng/button";
import { ToggleButtonModule } from "@bfx/primeng/togglebutton";
import { CheckboxModule } from "@bfx/primeng/checkbox";
import { DataTableModule } from "@bfx/primeng/datatable";
import { DropdownModule } from "@bfx/primeng/dropdown";
import { InputMaskModule } from "@bfx/primeng/inputmask";
import { TooltipModule } from "@bfx/primeng/tooltip";
import { InputTextModule } from "@bfx/primeng/inputtext";
import { SpinnerModule } from "@bfx/primeng/spinner";

/***********************/
/* BFX */
// modules
import { BfxSharedModule } from "../bfx-shared/bfx-shared.module";
import { BfxElementsLibraryModule } from "app/bfx-element-library/bfx-elements-library.module";
// services
import { BfxAutocompleteFavoritesProviderService } from "./interface/controls/services/bfx-autocomplete-favorites-provider.service";

// components
import { ICAutocompleteComponent } from "./interface/controls/ic-autocomplete.component";
// old controls
import { BfxPriceSpinnerComponent } from "./interface/controls/bfx-price-spinner.component";
import { BfxAmountSpinnerComponent } from "./interface/controls/bfx-amount-spinner.component";
import { BfxContactsInputComponent } from "./interface/controls/bfx-contacts-input.component";
import { BfxValueDateComponent } from "./interface/controls/bfx-value-date.component";
import { BfxSuspendControlComponent } from "./interface/controls/bfx-suspend-control.component";
import {
  BfxTimeInputComponent,
  INPUT_TIME_PATTERN,
  INPUT_TIME_MASK,
  INPUT_TIME_DEFAULT,
  INPUT_TIME_SHOWSECONDS,
  NOSECS_MASK,
  NOSECS_DEFAULT,
  NOSECS_VALIDATE_TIME_PATTERN,
} from "./interface/controls/bfx-time-input.component";
import { BfxMarkupComponent } from "./interface/controls/bfx-markup.component";
import { BfxClientIntentToggleComponent } from "./interface/controls/bfx-toggle-client-intent.component";
// good from until
import { BfxGoodFromUntilComponent } from "./interface/controls/good-from-until/bfx-good-from-until.component";
import { BfxCalendarComponent } from "./interface/controls/good-from-until/bfx-calendar.component";
// new - controls
import { ICExecutionModeComponent } from "./interface/controls/ic-execution-mode.component";
import { ICAllowPartialFillsComponent } from "./interface/controls/ic-allow-partial-fills.component";
import { ICHoldSTPComponent } from "./interface/controls/ic-hold-stp.component";
import { ICPrivateOrderComponent } from "./interface/controls/ic-private-order.component";
import { ICCustomFieldsComponent } from "./interface/controls/ic-custom-fields.component";
import { ICCustomFieldInstanceComponent } from "./interface/controls/ic-custom-field-instance.component";
// new standardized controls
import { ICAlgoTypeComponent } from "./interface/controls/ic-algo-type.component";
import { ICCcyPairStringComponent } from "./interface/controls/ic-ccy-pair-string.component";
import { NativeDesktopModule } from "../bfx-native-desktop/native-desktop-support.module";
import { ICAllowStpAggComponent } from "./interface/controls/ic-allow-stpagg.component";
import { ICAutoFwdPricingComponent } from "app/order-entry/interface/controls/ic-auto-fwd-pricing.component";
import { BfxTrailingPipsSpinnerComponent } from "./interface/controls/bfx-trailing-pips-spinner.component";
import { BfxTrailingTriggerSpinnerComponent } from "./interface/controls/bfx-trailing-trigger-spinner.component";
import { BaseComponentsModule } from "../../shared/base-components/base-components.module";
import { BfxPipeModule } from "../bfx-shared/bfx-pipes.module";
import { BfxNDFValueDateComponent } from "app/order-entry/interface/controls/bfx-ndf-value-date.component";

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    FlexLayoutModule,

    FormsModule,
    DataTableModule,
    InputTextModule,
    CheckboxModule,
    AutoCompleteModule,
    ButtonModule,
    ToggleButtonModule,
    SpinnerModule,
    DropdownModule,
    InputMaskModule,
    TooltipModule,
    BaseComponentsModule,
    BfxElementsLibraryModule,
    BfxSharedModule,
    NativeDesktopModule,
    BfxPipeModule,
  ],
  declarations: [
    ICAutocompleteComponent,
    BfxClientIntentToggleComponent,
    BfxPriceSpinnerComponent,
    BfxAmountSpinnerComponent,
    BfxContactsInputComponent,
    BfxValueDateComponent,
    BfxNDFValueDateComponent,
    BfxTrailingPipsSpinnerComponent,
    BfxTrailingTriggerSpinnerComponent,
    ICCustomFieldsComponent,
    ICCustomFieldInstanceComponent,
    BfxMarkupComponent,
    BfxGoodFromUntilComponent,
    BfxSuspendControlComponent,
    BfxTimeInputComponent,
    BfxCalendarComponent,
    ICExecutionModeComponent,
    ICAllowPartialFillsComponent,
    ICAllowStpAggComponent,
    ICHoldSTPComponent,
    ICAutoFwdPricingComponent,
    ICPrivateOrderComponent,
    ICAlgoTypeComponent,
    ICCcyPairStringComponent,
    ICCustomFieldsComponent,
    ICCustomFieldInstanceComponent,
  ],
  providers: [
    BfxAutocompleteFavoritesProviderService,
    { provide: INPUT_TIME_SHOWSECONDS, useValue: false },
    { provide: INPUT_TIME_PATTERN, useValue: NOSECS_VALIDATE_TIME_PATTERN },
    { provide: INPUT_TIME_MASK, useValue: NOSECS_MASK },
    { provide: INPUT_TIME_DEFAULT, useValue: NOSECS_DEFAULT },
  ],
  exports: [
    ICAutocompleteComponent,
    BfxClientIntentToggleComponent,
    BfxPriceSpinnerComponent,
    BfxTrailingPipsSpinnerComponent,
    BfxTrailingTriggerSpinnerComponent,
    BfxAmountSpinnerComponent,
    BfxContactsInputComponent,
    BfxValueDateComponent,
    BfxNDFValueDateComponent,
    //BfxFixingDateComponent,
    BfxMarkupComponent,
    BfxGoodFromUntilComponent,
    BfxSuspendControlComponent,
    BfxTimeInputComponent,
    BfxCalendarComponent,
    ICExecutionModeComponent,
    ICAllowPartialFillsComponent,
    ICAllowStpAggComponent,
    ICHoldSTPComponent,
    ICAutoFwdPricingComponent,
    ICPrivateOrderComponent,
    ICAlgoTypeComponent,
    ICCcyPairStringComponent,
    ICCustomFieldsComponent,
    ICCustomFieldInstanceComponent,
  ],
})
export class OEInterfaceControlsModule {}
